import React from "react";
import { Link } from "react-router-dom";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

/** Primary action in modal header (theme pill, opens new tab). */
export function ModalHeaderCreateLink({ to, label, iconClass, icon }) {
  const themeColor = useThemeColor();
  const faIcon = iconClass || icon;
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="text-decoration-none d-inline-flex align-items-center gap-1"
      style={{
        padding: "6px 12px",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: 600,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
        color: "#fff",
        border: "none",
        boxShadow: `0 2px 10px ${themeColor}45`,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.filter = "brightness(1.06)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      {faIcon ? (
        <i className={`fa ${faIcon}`} style={{ fontSize: "12px" }} aria-hidden />
      ) : null}
      <span>{label}</span>
      <i
        className="fa fa-external-link-alt"
        style={{ fontSize: "9px", opacity: 0.9 }}
        aria-hidden
      />
    </Link>
  );
}

/** Text button next to selection-modal search fields for refetching list data. */
export function SelectionModalRefreshButton({
  onClick,
  disabled = false,
  loading = false,
  label = "Refresh",
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  return (
    <button
      type="button"
      className="btn btn-sm d-inline-flex align-items-center gap-1 flex-shrink-0"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      style={{
        borderRadius: "8px",
        border: `1px solid ${isDarkMode ? "#555" : "#dee2e6"}`,
        background: isDarkMode ? "#404040" : "#fff",
        color: isDarkMode ? "#fff" : themeColor,
        fontWeight: 600,
        fontSize: "13px",
        padding: "8px 12px",
        whiteSpace: "nowrap",
        alignSelf: "stretch",
      }}
    >
      <i
        className={`fa fa-refresh${loading ? " fa-spin" : ""}`}
        style={{ fontSize: "12px" }}
        aria-hidden
      />
      <span>{label}</span>
    </button>
  );
}

/**
 * Use with CustomModal `headerActions={…}`.
 * @param {"category"|"reportingCategory"|"menuGroup"|"modifierGroup"|"supplier"|"serviceTime"} variant
 */
export function SelectionModalHeaderActions({ variant }) {
  const cfg = MODAL_LINK_CONFIG[variant];
  const header = cfg?.header;
  if (!header) return null;
  return <ModalHeaderCreateLink {...header} />;
}

export const MODAL_LINK_CONFIG = {
  category: {
    header: {
      to: "/categories/addCategory",
      label: "New category",
      icon: "fa-folder-plus",
    },
  },
  reportingCategory: {
    header: {
      to: "/manageReportingCategory",
      label: "Reporting categories",
      icon: "fa-sitemap",
    },
  },
  menuGroup: {
    header: { to: "/menu/addMenu", label: "New menu", icon: "fa-book" },
  },
  modifierGroup: {
    header: {
      to: "/modifiers/addModifierGroup",
      label: "New modifier group",
      icon: "fa-layer-group",
    },
  },
  supplier: {
    header: { to: "/Supplier/addSupplier", label: "New supplier", icon: "fa-truck" },
  },
  serviceTime: {
    header: {
      to: "/tableManagement/serviceTime",
      label: "New service time",
      icon: "fa-clock",
    },
  },
};

const EMPTY_STATE_COPY = {
  category: {
    title: "You don’t have any categories yet.",
    button: "Add first category",
  },
  reportingCategory: {
    title: "No reporting categories are set up yet.",
    button: "Set up reporting categories",
  },
  menuGroup: {
    title: "No menus or menu groups are available yet.",
    button: "Add first menu",
  },
  modifierGroup: {
    title: "You don’t have any modifier groups yet.",
    button: "Add first modifier group",
  },
  supplier: {
    title: "You don’t have any suppliers yet.",
    button: "Add first supplier",
  },
  serviceTime: {
    title: "No service times available yet.",
    button: "Create service time",
  },
};

/**
 * Shown when the backing list is empty (not a search miss).
 * @param {"category"|"reportingCategory"|"menuGroup"|"modifierGroup"|"supplier"|"serviceTime"} variant
 */
export function SelectionModalEmptyCTA({ variant }) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const header = MODAL_LINK_CONFIG[variant]?.header;
  const copy = EMPTY_STATE_COPY[variant];
  if (!header || !copy) return null;

  return (
    <div className="text-center py-4 px-3">
      <div
        className="d-inline-flex align-items-center justify-content-center mb-3 rounded-circle"
        style={{
          width: 64,
          height: 64,
          background: isDarkMode ? "#374151" : "#f1f5f9",
          border: `1px solid ${isDarkMode ? "#4b5563" : "#e2e8f0"}`,
        }}
      >
        <i
          className={`fa ${header.icon}`}
          style={{
            fontSize: 26,
            color: themeColor,
          }}
          aria-hidden
        />
      </div>
      <p
        className="mb-3"
        style={{
          fontSize: "15px",
          fontWeight: 600,
          color: isDarkMode ? "#f1f5f9" : "#334155",
          maxWidth: 320,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {copy.title}
      </p>
      <Link
        to={header.to}
        target="_blank"
        rel="noopener noreferrer"
        className="text-decoration-none d-inline-flex align-items-center gap-2"
        style={{
          padding: "12px 22px",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: 700,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          color: "#fff",
          boxShadow: `0 4px 14px ${themeColor}45`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.filter = "brightness(1.05)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.filter = "none";
          e.currentTarget.style.transform = "none";
        }}
      >
        <i className="fa fa-plus-circle" style={{ fontSize: "16px" }} aria-hidden />
        {copy.button}
        <i className="fa fa-external-link-alt" style={{ fontSize: "11px", opacity: 0.9 }} aria-hidden />
      </Link>
      <p
        className="mb-0 mt-3 small"
        style={{ color: isDarkMode ? "#94a3b8" : "#64748b", maxWidth: 340, margin: "12px auto 0" }}
      >
        Opens in a new tab. When you’re done, close it and reopen this picker (or refresh) to see new items.
      </p>
    </div>
  );
}
